import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  FaPlay, FaStop, FaTerminal, FaSyncAlt, FaRobot, FaEye, FaEyeSlash,
} from 'react-icons/fa';
import {
  TechButton, TechButtonGhost, TechSection,
} from '../../styles/techComponents';
import { activateDummyAccounts, getDummyActivityLogs } from '../../services/digitalProfilesService';

const Container = styled.div`
  background: var(--panel-bg);
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Title = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const LogContainer = styled.div`
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const LogEntry = styled.div`
  padding: 0.25rem 0;
  border-bottom: 1px solid #21262d;
  display: flex;
  gap: 0.5rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  ${props => props.$type === 'success' && `
    color: #38d39f;
  `}
  ${props => props.$type === 'error' && `
    color: #ff5470;
  `}
  ${props => props.$type === 'warning' && `
    color: #ffb347;
  `}
  ${props => props.$type === 'info' && `
    color: #58a6ff;
  `}
`;

const LogTime = styled.span`
  color: #8b949e;
  min-width: 85px;
  font-size: 0.75rem;
`;

const LogAccount = styled.span`
  color: #f0883e;
  min-width: 100px;
  font-weight: 500;
`;

const LogMessage = styled.span`
  flex: 1;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${props => props.$active ? `
    background: rgba(56, 211, 159, 0.2);
    color: #38d39f;
  ` : `
    background: rgba(139, 148, 158, 0.2);
    color: #8b949e;
  `}
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  font-style: italic;
`;

const ActivadorDummy = ({ profiles, onActivate, selectedIds = [] }) => {
  const [isActive, setIsActive] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [stats, setStats] = useState({ active: 0, total: 0 });
  const logsEndRef = useRef(null);
  const lastTimestampRef = useRef(null);

  // Usar todas las cuentas (para demo, todas funcionan como dummy)
  const dummyProfiles = profiles;

  // Scroll to bottom de logs
  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Cargar logs
  const loadLogs = useCallback(async () => {
    try {
      const since = lastTimestampRef.current || new Date(Date.now() - 60000).toISOString();
      const data = await getDummyActivityLogs({ since, limit: 50 });
      
      if (data.logs && data.logs.length > 0) {
        setLogs(prev => {
          const newLogs = [...prev, ...data.logs];
          // Mantener solo últimos 100
          return newLogs.slice(-100);
        });
        lastTimestampRef.current = data.timestamp;
      }
      
      // Actualizar stats
      const activeProfiles = dummyProfiles.filter(p => p.status === 'active');
      setStats({ active: activeProfiles.length, total: dummyProfiles.length });
    } catch (err) {
      console.error('Error cargando logs:', err);
    }
  }, [dummyProfiles]);

  // Auto-refresh de logs
  useEffect(() => {
    if (!autoRefresh) return;
    
    loadLogs();
    const interval = setInterval(loadLogs, 2000); // Cada 2 segundos
    
    return () => clearInterval(interval);
  }, [autoRefresh, loadLogs]);

  // Scroll cuando hay nuevos logs
  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Activar cuentas
  const handleActivate = async () => {
    setLoading(true);
    try {
      const idsToActivate = selectedIds.length > 0 
        ? selectedIds 
        : dummyProfiles.map(p => p.id);
      
      if (idsToActivate.length === 0) {
        alert('No hay cuentas dummy para activar');
        return;
      }

      const result = await activateDummyAccounts({
        action: 'start',
        accountIds: idsToActivate,
        interval: 30000, // 30 segundos entre actividades
      });

      // Agregar logs del resultado
      result.results.forEach(r => {
        const logEntry = {
          timestamp: new Date().toISOString(),
          accountId: r.account_id,
          username: r.username || `ID:${r.account_id}`,
          message: r.success ? '✅ Actividad iniciada' : `❌ ${r.error}`,
          type: r.success ? 'success' : 'error',
        };
        setLogs(prev => [...prev.slice(-99), logEntry]);
      });

      if (result.results.some(r => r.success)) {
        setIsActive(true);
        onActivate?.();
      }
    } catch (err) {
      console.error('Error activando cuentas:', err);
      const errorLog = {
        timestamp: new Date().toISOString(),
        accountId: 'system',
        username: 'Sistema',
        message: `❌ Error: ${err.message}`,
        type: 'error',
      };
      setLogs(prev => [...prev.slice(-99), errorLog]);
    } finally {
      setLoading(false);
    }
  };

  // Detener cuentas
  const handleStop = async () => {
    setLoading(true);
    try {
      const idsToStop = selectedIds.length > 0 
        ? selectedIds 
        : dummyProfiles.filter(p => p.status === 'active').map(p => p.id);

      if (idsToStop.length === 0) {
        alert('No hay cuentas activas para detener');
        return;
      }

      const result = await activateDummyAccounts({
        action: 'stop',
        accountIds: idsToStop,
      });

      result.results.forEach(r => {
        const logEntry = {
          timestamp: new Date().toISOString(),
          accountId: r.account_id,
          username: r.username || `ID:${r.account_id}`,
          message: r.success ? '🛑 Actividad detenida' : `❌ ${r.error}`,
          type: r.success ? 'warning' : 'error',
        };
        setLogs(prev => [...prev.slice(-99), logEntry]);
      });

      if (result.results.some(r => r.success)) {
        setIsActive(false);
      }
    } catch (err) {
      console.error('Error deteniendo cuentas:', err);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar logs
  const clearLogs = () => {
    setLogs([]);
    lastTimestampRef.current = null;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('es-AR', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false 
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <FaRobot /> Activador Automático de Dummy Accounts
        </Title>
        <Controls>
          <StatusBadge $active={isActive}>
            {isActive ? <><FaPlay size={10} /> ACTIVO</> : <><FaStop size={10} /> INACTIVO</>}
          </StatusBadge>
          <TechButton
            onClick={handleActivate}
            disabled={loading || dummyProfiles.length === 0}
            style={{ padding: '0.5rem 1rem' }}
          >
            {loading ? <FaSyncAlt className="spin" /> : <FaPlay />}
            {loading ? ' Activando...' : ' Activar'}
          </TechButton>
          <TechButtonGhost
            onClick={handleStop}
            disabled={loading}
            style={{ padding: '0.5rem 1rem' }}
          >
            <FaStop /> Detener
          </TechButtonGhost>
          <TechButtonGhost
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Pausar actualización' : 'Reanudar actualización'}
          >
            {autoRefresh ? <FaEye /> : <FaEyeSlash />}
          </TechButtonGhost>
          <TechButtonGhost
            onClick={clearLogs}
            title="Limpiar logs"
          >
            🗑️
          </TechButtonGhost>
        </Controls>
      </Header>

      <StatsRow>
        <StatItem>
          <strong>Dummy Accounts:</strong> {stats.total}
        </StatItem>
        <StatItem>
          <strong>Activas:</strong> {stats.active}
        </StatItem>
        <StatItem>
          <strong>Logs:</strong> {logs.length}
        </StatItem>
        <StatItem>
          <strong>Auto-refresh:</strong> {autoRefresh ? 'ON' : 'OFF'}
        </StatItem>
      </StatsRow>

      <LogContainer>
        {logs.length === 0 ? (
          <EmptyState>
            <FaTerminal size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <div>No hay actividad registrada</div>
            <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
              Click en "Activar" para iniciar la simulación
            </div>
          </EmptyState>
        ) : (
          logs.map((log, idx) => (
            <LogEntry key={idx} $type={log.type}>
              <LogTime>{formatTime(log.timestamp)}</LogTime>
              <LogAccount>@{log.username}</LogAccount>
              <LogMessage>{log.message}</LogMessage>
            </LogEntry>
          ))
        )}
        <div ref={logsEndRef} />
      </LogContainer>

      <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        💡 <strong>Nota:</strong> Este sistema simula actividad real en las cuentas dummy (scrolls, likes, 
        views, comentarios). Las cuentas seleccionadas iniciarán sesión automáticamente y generarán 
        actividad cada 30 segundos. Los logs se actualizan en tiempo real cada 2 segundos.
      </div>
    </Container>
  );
};

export default ActivadorDummy;
